import { Link, useParams } from "react-router-dom";
import { specials } from "@/mocks/specials";
import { getNewsById, getVideoById, formatViews, getVideoTypeName, getCategoryName } from "@/lib/content";
import { usePageTitle } from "@/hooks/usePageTitle";

export default function SpecialPage() {
  const { id = "" } = useParams();
  const special = specials.find((s) => s.id === id);

  usePageTitle(
    special ? `${special.title} - 热点专题 - 91.con` : "热点专题 - 91.con",
    special ? special.intro : undefined,
  );

  if (!special) {
    return (
      <div className="mx-auto flex w-full flex-col items-center gap-4 px-4 py-24">
        <i className="ri-error-warning-line text-4xl text-primary-400"></i>
        <p className="text-foreground-700">未找到该专题。</p>
        <Link
          to="/"
          className="rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const newsItems = special.items
    .filter((i) => i.kind === "news")
    .map((i) => getNewsById(i.id))
    .filter((n): n is NonNullable<typeof n> => Boolean(n));

  const videoItems = special.items
    .filter((i) => i.kind === "video")
    .map((i) => getVideoById(i.id))
    .filter((v): v is NonNullable<typeof v> => Boolean(v));

  const others = specials.filter((s) => s.id !== special.id).slice(0, 3);

  return (
    <div className="w-full">
      {/* 专题大图 */}
      <section className="relative h-[300px] w-full overflow-hidden md:h-[420px]">
        <img
          src={special.cover}
          alt={`${special.title} - 专题大图`}
          title={`${special.title} - 91.con热点专题`}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/70 to-background-50/20"></div>
        <div className="absolute inset-0 flex items-end">
          <div className="mx-auto w-full px-4 pb-8 md:px-6 md:pb-12">
            <nav className="mb-4 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-400">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800">热点专题</span>
            </nav>
            <div className="mb-3 flex flex-wrap gap-2">
              {special.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-sm bg-accent-500 px-2 py-0.5 text-xs font-medium text-background-50"
                >
                  {t}
                </span>
              ))}
            </div>
            <h1 className="font-heading text-3xl font-bold text-foreground-950 md:text-5xl">
              {special.title}
            </h1>
            <p className="mt-3 max-w-3xl text-sm text-foreground-700 md:text-base">{special.intro}</p>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
        {/* 编辑点评 */}
        <div className="mb-10 rounded-lg border border-primary-400/30 bg-primary-100/30 p-6">
          <div className="mb-2 flex items-center gap-2">
            <i className="ri-quill-pen-line text-primary-400"></i>
            <span className="font-heading text-sm font-bold uppercase tracking-widest text-primary-300">
              编辑点评
            </span>
          </div>
          <p className="text-base leading-relaxed text-foreground-800">{special.editorNote}</p>
        </div>

        {/* 专题内容集合 */}
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="min-w-0">
            <h2 className="mb-5 flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
              <span className="h-5 w-1 rounded-full bg-primary-500"></span>专题资讯
            </h2>
            <div className="flex flex-col gap-4">
              {newsItems.map((n) => (
                <Link
                  key={n.id}
                  to={`/news/${n.id}`}
                  className="group flex flex-col gap-4 overflow-hidden rounded-lg border border-background-200 bg-background-100 p-4 transition-colors hover:border-primary-400/60 sm:flex-row"
                >
                  <div className="h-40 w-full flex-shrink-0 overflow-hidden rounded-md sm:h-28 sm:w-44">
                    <img
                      src={n.cover}
                      alt={`${n.title} - 专题资讯`}
                      title={`${n.title} - 91.con娱乐资讯`}
                      className="h-full w-full object-cover transition-transform group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0">
                    <span className="rounded-sm bg-primary-500/15 px-2 py-0.5 text-xs font-medium text-primary-300">
                      {getCategoryName(n.category)}
                    </span>
                    <h3 className="mt-2 line-clamp-2 font-heading text-lg font-semibold text-foreground-950 transition-colors group-hover:text-primary-400">
                      {n.title}
                    </h3>
                    <p className="mt-1 line-clamp-2 text-sm text-foreground-600">{n.summary}</p>
                    <div className="mt-2 flex items-center gap-4 text-xs text-foreground-500">
                      <span>{n.date}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {formatViews(n.views)}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <h2 className="mb-5 mt-10 flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
              <span className="h-5 w-1 rounded-full bg-accent-500"></span>专题视频
            </h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {videoItems.map((v) => (
                <Link
                  key={v.id}
                  to={`/video/${v.id}`}
                  className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-colors hover:border-accent-400/60"
                >
                  <div className="relative h-44 w-full overflow-hidden">
                    <img
                      src={v.cover}
                      alt={`${v.title} - 专题视频`}
                      title={`${v.title} - 91.con娱乐视频`}
                      className="h-full w-full object-cover transition-transform group-hover:scale-105"
                    />
                    <span className="absolute bottom-3 right-3 rounded-sm bg-background-50/85 px-2 py-0.5 text-xs font-medium text-foreground-950">
                      {v.duration}
                    </span>
                    <span className="absolute left-3 top-3 rounded-sm bg-secondary-500/20 px-2 py-0.5 text-xs font-medium text-secondary-300">
                      {getVideoTypeName(v.type)}
                    </span>
                  </div>
                  <div className="p-4">
                    <h3 className="line-clamp-2 font-heading text-base font-semibold text-foreground-950 transition-colors group-hover:text-accent-400">
                      {v.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* 相关专题 */}
          <aside>
            <h2 className="mb-5 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <i className="ri-fire-line text-primary-400"></i>相关专题
            </h2>
            <div className="flex flex-col gap-4">
              {others.map((s) => (
                <Link
                  key={s.id}
                  to={`/special/${s.id}`}
                  className="group flex gap-3 overflow-hidden rounded-lg border border-background-200 bg-background-100 p-3 transition-colors hover:border-primary-400/60"
                >
                  <div className="h-16 w-24 flex-shrink-0 overflow-hidden rounded-md">
                    <img
                      src={s.cover}
                      alt={`${s.title} - 相关专题`}
                      title={`${s.title} - 91.con热点专题`}
                      className="h-full w-full object-cover transition-transform group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0">
                    <h3 className="line-clamp-2 font-heading text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-400">
                      {s.title}
                    </h3>
                    <span className="mt-1 flex items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-links-line"></i>
                      {s.items.length} 篇内容
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5 text-sm text-foreground-600">
              <i className="ri-information-line mr-1 text-accent-400"></i>
              专题内容为前端演示数据，待后端数据接口接入后可持续扩展。
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}